import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { addDailyReport, editDailyReport } from "services/caseServices";
import {
  CaseCreation,
  DailyReport,
  DailyReportStatus,
  OpeniningCaseFile,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  caseData: CaseCreation;
  onClose: (refetch: boolean) => void;
};

const defaultValue: DailyReport = {
  id: 0,
  opening_case_file: 0,
  defendants: [],
  status: null,
  prosecutor_registration_number: "",
  incident_registration_no: "",
  crime_refernce_number: "",
  court_registration_number: "",
  next_trial_appointment: null,
  appointment_type: "",
  appointment_reason: "",
  dailyreport_summary: "",
  court_order_for_next_appointment: "",
  is_final_verdict_given: false,
};

const AddDailyReport: FC<Props> = ({ caseData, action, onClose }) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState(defaultValue);

  useEffect(() => {
    if (caseData) {
      const opening = caseData?.opening_case_files?.[0] as OpeniningCaseFile;
      setFormState({
        ...defaultValue,
        prosecutor_registration_number:
          caseData?.prosecutor_registration_number || "",
        incident_registration_no: caseData?.incident_registration_no || "",
        crime_refernce_number: caseData?.crime_refernce_number || "",
        court_registration_number: opening?.court_registration_number || "",
        opening_case_file: opening?.id || 0,
      });
    }
  }, [caseData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (formState.next_trial_appointment) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const trialDate = new Date(formState.next_trial_appointment);
      trialDate.setHours(0, 0, 0, 0);
      if (trialDate <= today) {
        return "Next trial appointment must be greater than today.";
      }
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Submit the FormData
    const submitAction = action === "Edit" ? editDailyReport : addDailyReport;
    submitAction({
      ...formState,
      opening_case_file: caseData?.opening_case_files?.[0]?.id ?? 0,
      case: caseData?.id ?? 0,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success"
        );
        setFormState(defaultValue);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error"
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">Fill Daily Report for</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          Lorem Ipsum is a placeholder text commonly used in the design and
          publishing industries as a temporary filler.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="mt-4">
            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Defendant
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                value={defendantId}
                onChange={(e) => {
                  const value = e.target.value;
                  setDefendantId(value);
                }}
              >
                <option value={""}>Select Defendant</option>
                {caseData?.case_defendant_links?.map((def: any) => (
                  <option key={def.id} value={def.id}>
                    Defendant {def.defendant_number} :{" "}
                    {def.defendant_info?.first_name}{" "}
                    {def.defendant_info?.middle_name}{" "}
                    {def.defendant_info?.last_name}
                  </option>
                ))}
              </select>
            </div> */}
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Report Date
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="report_date"
                value={
                  formState?.report_date
                    ? new Date(formState.report_date)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                min={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Appointment Type
              </label>
              <input
                type="text"
                placeholder="Appointment type here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="appointment_type"
                value={formState?.appointment_type}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Appointment Reason
              </label>
              <input
                type="text"
                placeholder="Appointment reason here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="appointment_reason"
                value={formState?.appointment_reason}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Daily Report Summary
              </label>
              <textarea
                rows={4}
                placeholder="Daily report detail here..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="dailyreport_summary"
                value={formState?.dailyreport_summary}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Court Order for Next Appointment
              </label>
              <textarea
                rows={4}
                placeholder="Court order for next appointment..."
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="court_order_for_next_appointment"
                value={formState?.court_order_for_next_appointment}
                onChange={handleChange}
              />
            </div>

            {/* <div className="mt-4">
              <FileUpload
                label="Other Attachment Files"
                fileTypes="image/*, .pdf"
                info="Upload PDF or Image Files "
                // required={true}
                onChange={handleFile}
              />
            </div> */}
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Next Trial Appointment
              </label>
              <input
                type="date"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="next_trial_appointment"
                value={
                  formState?.next_trial_appointment
                    ? new Date(formState.next_trial_appointment)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                min={new Date().toISOString().split("T")[0]}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Select Status
              </label>
              <select
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                name="status"
                value={formState?.status || ""}
                onChange={handleChange}
              >
                <option value={""}>Select Status</option>
                {Object.entries(DailyReportStatus).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center mt-4 gap-2 text-sm text-gray-600 font-[inter] font-[400] mb-2">
              <input
                type="checkbox"
                className="bg-[#F2F2F2] rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                checked={Boolean(formState?.is_final_verdict_given)}
                onChange={() =>
                  setFormState((prevState) => ({
                    ...prevState,
                    is_final_verdict_given: !prevState.is_final_verdict_given,
                  }))
                }
              />
              Is Final Verdict Given
            </label>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Set Appointment"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDailyReport;
